// src/components/test/ScaleAnswer.tsx
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../common/Button';
import { ScaleConfig } from '../../types/testData';
import { useLang } from '../../context/LangContext';

const ScaleWrap = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 8px auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ScaleTrack = styled.div`
  position: relative;
  width: 100%;
  height: 56px;
`;

// Нативный range-инпут растянут на всю ширину; бегунок — «плашка», метки — ниже с зазором.
const ScaleInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  margin: 0;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const TickLabel = styled.span`
  position: absolute;
  top: 34px; /* зазор между линией бегунка и числами */
  transform: translateX(-50%);
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  user-select: none;
  pointer-events: none;
  white-space: nowrap;
`;

const PolesRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

interface ScaleAnswerProps {
  config: ScaleConfig;
  /** Выбранное ранее значение (при возврате/восстановлении черновика). */
  value?: number;
  disabled?: boolean;
  /** Подтверждение «Далее»: передаёт выбранное значение (балл шкалы). */
  onConfirm: (value: number) => void;
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

export const ScaleAnswer: React.FC<ScaleAnswerProps> = ({
  config,
  value,
  disabled = false,
  onConfirm,
}) => {
  const { t } = useLang();
  // Середина шкалы (значение, на которое ставится подпись центра и начальное положение бегунка).
  const midValue = useMemo(() => {
    const mid = (config.min + config.max) / 2;
    return Math.round(mid / config.step) * config.step;
  }, [config.min, config.max, config.step]);

  // Начальное положение — значение, ближайшее к середине шкалы, иначе левый край.
  const initial = useMemo(() => {
    if (value !== undefined && Number.isFinite(value)) {
      const clamped = Math.min(config.max, Math.max(config.min, value));
      return Math.round(clamped / config.step) * config.step;
    }
    if (midValue >= config.min && midValue <= config.max) {
      return midValue;
    }
    return config.min;
  }, [value, config.min, config.max, config.step, midValue]);

  const [selected, setSelected] = useState<number>(initial);

  // Метки-плашки: от min до max с шагом step.
  const ticks = useMemo(() => {
    const arr: number[] = [];
    const steps = Math.round((config.max - config.min) / config.step);
    for (let i = 0; i <= steps; i++) {
      const v = Math.round((config.min + i * config.step) * 100) / 100;
      arr.push(v);
    }
    return arr;
  }, [config.min, config.max, config.step]);

  const posPct = (v: number) => {
    const raw = ((v - config.min) / (config.max - config.min)) * 100;
    // Крайние плашки не должны вылезать за границы дорожки.
    return Math.min(98, Math.max(2, raw)).toFixed(2);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    setSelected(Math.round(v / config.step) * config.step);
  };

  return (
    <ScaleWrap>
      <ScaleTrack>
        <ScaleInput
          type="range"
          min={config.min}
          max={config.max}
          step={config.step}
          value={selected}
          disabled={disabled}
          onChange={handleChange}
        />
        {ticks.map((v) => (
          <TickLabel
            key={v}
            style={{
              left: `${posPct(v)}%`,
              fontWeight: v === midValue && config.midLabel ? 700 : 400,
            }}
          >
            {v === midValue ? config.midLabel : fmtNum(v)}
          </TickLabel>
        ))}
      </ScaleTrack>

      <PolesRow>
        <span>{config.leftLabel || '−'}</span>
        <span>{config.rightLabel || '+'}</span>
      </PolesRow>

      <Button
        variant="primary"
        size="md"
        disabled={disabled}
        onClick={() => onConfirm(selected)}
        fullWidth
        style={{ maxWidth: 280 }}
      >
        {t('scaleConfirm')}
      </Button>
    </ScaleWrap>
  );
};